import { EnvironmentProviders, Provider } from '@angular/core';

import { Capacitor } from '@capacitor/core';

import { ANALYTICS_PRIVACY_POLICY_LINK, provideDevAnalytics } from '@guardfinder/analytics';
import { isDevelopmentEnvironment, provideSharedAnalytics } from '@guardfinder/shared';

import { environment } from '../../environments/environment';
import { Globals } from '../shared/globals';

/**
 * Provides analytics-related services and configurations required by the application.
 *
 * Depending on the environment, this function returns a list of providers tailored for analytics integration.
 * If the environment is identified as a development environment, development-specific analytics providers will be returned.
 * Otherwise, it provides common analytics resources and configurations, ensuring the application works in both web and native platforms.
 *
 * The `privacyPolicyProvider` supplies the URL for the analytics privacy policy using a factory mechanism that retrieves the data from the global `Globals` service.
 * The returned providers also include platform-specific settings, such as support for native platforms via Capacitor, and Firebase configuration for analytics tracking.
 *
 * @returns {Array<Provider | EnvironmentProviders>} A collection of providers for analytics configuration.
 */
export const provideAnalytics = (): Array<Provider | EnvironmentProviders> => {
    if (isDevelopmentEnvironment(environment)) {
        return provideDevAnalytics();
    }

    /**
     * Provides the privacy policy URL for the analytics module.
     *
     * This provider supplies the `ANALYTICS_PRIVACY_POLICY_LINK` injection token with the privacy policy URL obtained from the `Globals` service.
     * The analytics module is application-independent and isolated, so it cannot directly access globals.
     * This provider uses a factory to inject the privacy policy URL as a string, which is then used by components such as `AnalyticsConsentModalComponent`.
     */
    const privacyPolicyProvider: Provider = {
        provide: ANALYTICS_PRIVACY_POLICY_LINK,
        useFactory: (globals: Globals) => globals.ppUrl,
        deps: ['globals'] // Declare Globals as a dependency
    };

    const isNativePlatform = Capacitor.isNativePlatform();
    const firebaseConfig = environment.firebaseConfig;
    return provideSharedAnalytics(isNativePlatform, firebaseConfig, privacyPolicyProvider);
};
